import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

// ─── *appLazyLoad ─────────────────────────────────────────────────────────
// Renderiza o conteúdo do template SÓ quando o placeholder entra (ou se
// aproxima de) viewport, via IntersectionObserver. Útil pra adiar componentes
// abaixo da dobra (revisões, discussões, etc.) que fazem fetch em ngOnInit.
//
// Uso:
//   <ng-container *appLazyLoad>
//     <app-componente-pesado [foo]="bar"></app-componente-pesado>
//   </ng-container>
//
// Comportamento:
//   • Insere um placeholder <div> de 1px na posição do directive
//   • Observa via IntersectionObserver com `rootMargin` configurável
//     (default 300px — começa a montar 300px antes de aparecer; fica
//     pronto até o user rolar até lá, sem "pop-in" perceptível)
//   • Ao 1º intersect, renderiza o conteúdo, remove o placeholder, e
//     desconecta o observer (one-shot — sem flickering ao rolar pra
//     fora/voltar)
//   • Cleanup garantido em ngOnDestroy
// ─────────────────────────────────────────────────────────────────────────

// Evento global p/ FORÇAR todas as diretivas lazy montadas a renderizar já (sem
// esperar o scroll). Usado pela página de submissão p/ carregar revisões/discussão
// proativamente. `dispatchEvent(new CustomEvent(FORCE_LAZY_RENDER_EVENT))`.
export const FORCE_LAZY_RENDER_EVENT = 'jems:force-lazy-render';

@Directive({
  selector: '[appLazyLoad]',
})
export class LazyLoadDirective implements OnInit, OnDestroy {
  @Input() appLazyLoadRootMargin = '300px';

  private observer?: IntersectionObserver;
  private placeholder?: HTMLElement;
  private rendered = false;
  private readonly onForceRender = () => { if (!this.rendered) { this.render(); } };

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    window.addEventListener(FORCE_LAZY_RENDER_EVENT, this.onForceRender);

    // Fallback: navegadores sem IntersectionObserver → renderiza imediato.
    if (typeof IntersectionObserver === 'undefined') {
      this.render();
      return;
    }

    // O anchor do directive (`viewContainer.element`) é um Comment node, que
    // não tem layout — IntersectionObserver não funciona com ele. Inserimos
    // um placeholder real, observamos, depois removemos ao renderizar.
    const anchor = this.viewContainer.element.nativeElement as Node;
    const placeholder = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setStyle(placeholder, 'min-height', '1px');
    this.renderer.setAttribute(placeholder, 'aria-hidden', 'true');
    this.renderer.setAttribute(placeholder, 'data-lazy-load-placeholder', '');
    anchor.parentNode?.insertBefore(placeholder, anchor);
    this.placeholder = placeholder;

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !this.rendered) {
          this.render();
        }
      },
      { rootMargin: this.appLazyLoadRootMargin },
    );
    this.observer.observe(placeholder);
  }

  ngOnDestroy(): void {
    window.removeEventListener(FORCE_LAZY_RENDER_EVENT, this.onForceRender);
    this.observer?.disconnect();
    this.placeholder?.remove();
  }

  private render(): void {
    this.rendered = true;
    this.observer?.disconnect();
    this.observer = undefined;
    this.placeholder?.remove();
    this.placeholder = undefined;
    this.viewContainer.createEmbeddedView(this.templateRef);
  }
}
